import { useEffect, useRef, useState, type ReactNode } from "react";

type SeiteProps = {
  titel: string;
  /** Links in der Leiste (Zurueck/„Fertig“), rechts hoechstens zwei Aktionen. */
  links?: ReactNode;
  rechts?: ReactNode;
  /** Unter dem grossen Titel (Suchfeld, Segment), scrollt mit. */
  kopf?: ReactNode;
  /** Inhaltsbreite: Liste 720, Lesbreite 672, Detail 900 (Designsprache-Tokens 3.3), breit 1120 fuer
   *  Zweispalter am Desktop (Posteingang, Dokument mit Panel; Plan 6.5). */
  breite?: "liste" | "lesen" | "detail" | "breit";
  /** Nur kompakte Leiste, kein grosser Titel (Detailansichten). */
  kompakt?: boolean;
  /** W82 (E14): der grosse Titel steht oben nur fuer Vorlesen, beim Rollen klein in der Leiste wie sonst. */
  titelVorlesen?: boolean;
  children: ReactNode;
};

/** W82 Web-8 W8-5 (F-S2 = A, D-1209, D-1210): über diesen Rollweg (px) geht der Kopf der Welt von offen in Glas über, so weit wie
 *  der Rand, ab dem die Leiste als gerollt gilt (`rootMargin` unten) */
const GLAS_ROLLWEG = 44;

const BREITE = { liste: "max-w-(--w-list)", lesen: "max-w-(--w-readable)", detail: "max-w-(--w-detail)", breit: "max-w-(--w-breit)" };

/** Seite mit Navigationsleiste (Designsprache-Tokens 8.1): Kopfzeile deckend auf --pg-bg (Schiefer,
 *  W22b), grosser Titel; sobald Inhalt unter die Leiste scrollt, Haarlinie und kompakter Titel. */
export function Seite({ titel, links, rechts, kopf, breite = "liste", kompakt, titelVorlesen, children }: SeiteProps) {
  const sentinel = useRef<HTMLDivElement>(null);
  const kopfRef = useRef<HTMLElement>(null);
  const [gescrollt, setGescrollt] = useState(false);

  // W82 Web-8 W8-5: Anteil des Glases nach der Rolllage (0 in Ruhe ganz oben, 1 ab GLAS_ROLLWEG), direkt am Kopf gesetzt, damit
  // die Seite beim Rollen nicht neu zeichnet; welt.css blendet damit die Glasebene über den Rollweg ein (kein harter Wechsel).
  // `data-glas` nur, solange etwas Glas steht: in Ruhe gibt es die Ebene mit Unschärfe gar nicht (sonst eine eigene
  // Compositing-Ebene, die am Schreibtisch die Kantenglättung der Schrift ändert; Ruhe bildgleich zur Basis)
  useEffect(() => {
    const kopf = kopfRef.current;
    if (!kopf) return;
    const setzen = () => {
      const anteil = Math.min(1, Math.max(0, window.scrollY / GLAS_ROLLWEG));
      kopf.style.setProperty("--leiste-glas", String(anteil));
      kopf.toggleAttribute("data-glas", anteil > 0);
    };
    setzen();
    window.addEventListener("scroll", setzen, { passive: true });
    return () => window.removeEventListener("scroll", setzen);
  }, []);

  useEffect(() => {
    const ziel = sentinel.current;
    // ohne IntersectionObserver (alte Engine) bleibt der große Titel stehen, statt dass die Seite abbricht (W82-Web-A10)
    if (!ziel || typeof IntersectionObserver === "undefined") return;
    const beobachter = new IntersectionObserver(([e]) => setGescrollt(!e?.isIntersecting), {
      rootMargin: "-44px 0px 0px 0px",
    });
    beobachter.observe(ziel);
    return () => beobachter.disconnect();
  }, []);

  useEffect(() => {
    document.title = `${titel} · Papiergaist`;
  }, [titel]);

  const titelKompakt = kompakt || gescrollt;
  return (
    <div className="min-h-dvh pb-(--tab-abstand) md:pb-16" data-raster-ausnahme="tab-abstand">
      <header
        ref={kopfRef}
        className={`leiste sticky top-0 z-30 bg-pg-bg pt-[env(safe-area-inset-top)] ${gescrollt ? "leiste-linie" : ""}`}
        data-leiste={gescrollt ? "material" : "ruhe"}
      >
        <div className={`rand mx-auto grid h-(--bar-h) grid-cols-[1fr_auto_1fr] items-center gap-2 md:h-(--bar-h-desktop) ${BREITE[breite]}`}>
          <div className="flex items-center justify-self-start">{links}</div>
          <span
            className={`t-headline min-w-0 truncate transition-opacity duration-(--dur-title) ${titelKompakt ? "opacity-100" : "opacity-0"}`}
            aria-hidden={!titelKompakt}
          >
            {titel}
          </span>
          <div className="flex items-center gap-1 justify-self-end">{rechts}</div>
        </div>
      </header>
      <div ref={sentinel} aria-hidden className="h-px" />
      <main className={`rand mx-auto ${BREITE[breite]}`}>
        {kompakt || titelVorlesen ? (
          <h1 className="sr-only outline-none" tabIndex={-1}>
            {titel}
          </h1>
        ) : (
          <div className="pt-2 pb-2">
            <h1 className="t-large-title outline-none" tabIndex={-1}>
              {titel}
            </h1>
          </div>
        )}
        {kopf && <div className="mt-2 space-y-3">{kopf}</div>}
        {children}
      </main>
    </div>
  );
}
